import { Link, useParams } from "react-router-dom";
import { useCustomer } from "@/lib/queries";
import { addressLine, SERVICE_STATUS_LABELS } from "@/lib/format";
import { followUp, deliveryText } from "@/lib/followup";
import { LoadingBlock, ErrorBlock, Breadcrumbs, Section } from "@/components/common/Blocks";
import { PhaseBadge, StateChip, toneForServiceStatus } from "@/components/common/Badges";
import { CompanyLogo, customerKind } from "@/components/common/CompanyMark";

const Rows = ({ rows }) => (
  <div className="frame overflow-x-auto"><table className="data-table text-[14.5px]">
    <thead><tr><th>Leveranse</th><th className="w-[130px]">Fase</th><th className="hidden w-[110px] md:table-cell">Levering</th><th className="hidden lg:table-cell">Oppfølging</th></tr></thead>
    <tbody>
      {rows.map((d) => {
        const del = deliveryText(d.dates);
        const fu = followUp(d);
        return (
          <tr key={d.id}>
            <td><Link to={`/leveranser/${d.id}`} className="font-semibold hover:text-primary hover:underline">{d.project_name}</Link><p className="t-meta mt-0.5">{d.number} · {d.stair_summary}</p></td>
            <td><PhaseBadge phase={d.phase} /></td>
            <td className="hidden tabular md:table-cell">{del.main}{del.sub && <span className="t-meta"> {del.sub}</span>}</td>
            <td className={`hidden lg:table-cell ${fu.kind === "handling" ? "" : "text-muted-foreground"}`}>{fu.title}</td>
          </tr>
        );
      })}
    </tbody>
  </table></div>
);

export default function KundeDetaljPage() {
  const { id } = useParams();
  const { data: c, isLoading, isError, refetch } = useCustomer(id);
  if (isLoading) return <div className="page"><LoadingBlock /></div>;
  if (isError || !c) return <div className="page"><ErrorBlock message="Fant ikke kunden." onRetry={refetch} /></div>;
  const active = c.deliveries.filter((d) => d.phase !== "levert");
  const delivered = c.deliveries.filter((d) => d.phase === "levert");
  return (
    <div className="page" data-testid="customer-detail-page">
      <Breadcrumbs items={[{ label: "Kunder", to: "/kunder" }, { label: c.name }]} />
      <header className="mb-6 flex items-start gap-4">
        <CompanyLogo name={c.name} type={c.type} logo={c.logo_url} website={c.website} className="mt-0.5" testId="customer-mark" />
        <div className="min-w-0">
          <h1 className="h-page">{c.name}</h1>
          <p className="mt-1 text-[15px]">{customerKind(c)}{c.org_no ? ` · org.nr. ${c.org_no}` : ""} · {addressLine(c.address)}{c.website ? <> · <a href={`https://${c.website}`} target="_blank" rel="noreferrer" className="link" data-testid="customer-website">{c.website}</a></> : null}</p>
          {c.note && <p className="t-meta mt-1">{c.note}</p>}
        </div>
      </header>
      <div className="grid grid-cols-1 gap-x-10 gap-y-8 lg:grid-cols-12">
        <div className="space-y-8 lg:col-span-4">
          <Section title="Kontakter" testId="customer-contacts">
            <ul className="divide-y">
              {c.contacts.map((p, i) => (
                <li key={i} className="py-2.5 text-[14.5px]">
                  <p className="font-medium">{p.name} <span className="t-meta font-normal">· {p.role}</span></p>
                  {p.phone && <a href={`tel:${p.phone.replace(/\s/g, "")}`} className="block text-muted-foreground hover:text-foreground">{p.phone}</a>}
                  {p.email && <a href={`mailto:${p.email}`} className="block text-muted-foreground hover:text-foreground">{p.email}</a>}
                </li>
              ))}
            </ul>
          </Section>
          {c.service_cases.length > 0 && (
            <Section title="Serviceoppdrag">
              <ul className="divide-y">{c.service_cases.map((s) => <li key={s.id}><Link to={`/service/${s.id}`} className="list-row list-row-link text-[14.5px]"><span className="min-w-0 flex-1 truncate">{s.title} <span className="t-meta">{s.number}</span></span><StateChip tone={toneForServiceStatus(s.status)}>{SERVICE_STATUS_LABELS[s.status]}</StateChip></Link></li>)}</ul>
            </Section>
          )}
        </div>
        <div className="space-y-8 lg:col-span-8">
          <Section title="Aktive leveranser" testId="customer-active-deliveries" right={active.length}>{active.length ? <Rows rows={active} /> : <p className="t-body text-muted-foreground">Ingen aktive leveranser.</p>}</Section>
          <Section title="Tidligere leveranser" testId="customer-delivered" right={delivered.length}>{delivered.length ? <Rows rows={delivered} /> : <p className="t-body text-muted-foreground">Ingen tidligere leveranser.</p>}</Section>
        </div>
      </div>
    </div>
  );
}
